import React, { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import StarField from "../components/StarField";
import ContactInbox from "../components/ContactInbox";
import { api, setSeo } from "../lib/api";

function todayStr() {
  const d = new Date();
  // Use local-ish IST date for default
  const ist = new Date(d.getTime() + (330 + d.getTimezoneOffset()) * 60000);
  return ist.toISOString().slice(0, 10);
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("sle_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loading, setLoading] = useState(false);

  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [inputs, setInputs] = useState({});
  const [autoMode, setAutoMode] = useState(false);
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState("results");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    setSeo("Admin Panel - Shillong Lucky Ending", "Secure admin panel to manage Shillong Lucky Ending results.");
  }, []);

  const loadResults = useCallback(async (d) => {
    try {
      const { data } = await api.get(`/admin/results`, { params: { date: d } });
      setSlots(data.time_slots);
      const map = {};
      data.results.forEach((r) => {
        map[r.time_slot] = r.number === null || r.number === undefined ? "" : String(r.number);
      });
      setInputs(map);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setAutoMode(data.auto_mode);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  }, []);

  useEffect(() => {
    if (authed) {
      loadResults(date);
      loadSettings();
      api.get("/admin/messages").then(({ data }) => setUnread(data.unread)).catch(() => {});
    }
  }, [authed, date, loadResults, loadSettings]);

  const login = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("sle_token", data.token);
      setAuthed(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginErr(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("sle_token");
    setAuthed(false);
  };

  const flash = (t) => {
    setMsg(t);
    setTimeout(() => setMsg(""), 2500);
  };

  const saveSlot = async (slot) => {
    const val = inputs[slot];
    if (val === "" || val === undefined) return;
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0 || n > 9) {
      flash("Enter a digit 0-9");
      return;
    }
    await api.post("/admin/results", { date, time_slot: slot, number: n });
    flash(`Saved ${slot} → ${n}`);
  };

  const clearSlot = async (slot) => {
    await api.delete("/admin/results", { params: { date, time_slot: slot } });
    setInputs((p) => ({ ...p, [slot]: "" }));
    flash(`Cleared ${slot}`);
  };

  const toggleAuto = async () => {
    const next = !autoMode;
    setAutoMode(next);
    await api.post("/admin/settings", { auto_mode: next });
    flash(next ? "Auto mode ON" : "Auto mode OFF");
  };

  const autoFill = async () => {
    const { data } = await api.post("/admin/auto-fill-today");
    flash(`Auto-filled ${data.filled} slot(s)`);
    loadResults(date);
  };

  if (!authed) {
    return (
      <div data-testid="admin-login-page">
        <StarField />
        <Header />
        <div className="admin-wrap" style={{ maxWidth: 420 }}>
          <div className="admin-card">
            <div className="admin-title">Admin Login</div>
            <div className="admin-sub">Enter your credentials to manage results</div>
            <form onSubmit={login}>
              <label className="admin-label">Username</label>
              <input
                className="admin-input"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@shillong.com"
                data-testid="admin-email"
                style={{ marginBottom: 16 }}
              />
              <label className="admin-label">Password</label>
              <input
                className="admin-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-password"
                style={{ marginBottom: 8 }}
              />
              <button className="admin-btn" type="submit" disabled={loading} data-testid="admin-login-btn">
                {loading ? "Signing in…" : "Sign In"}
              </button>
              {loginErr && <div className="admin-err" data-testid="login-error">{loginErr}</div>}
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard">
      <StarField />
      <Header />
      <div className="admin-wrap">
        <div className="admin-card">
          <div className="admin-toolbar">
            <div>
              <div className="admin-title">{tab === "results" ? "Result Manager" : "Contact Inbox"}</div>
              <div className="admin-sub">
                {tab === "results" ? "Enter or edit Lucky Ending digits per time slot" : "Messages sent via the Contact Us page"}
              </div>
            </div>
            <button className="admin-btn secondary" style={{ width: "auto", padding: "10px 20px", margin: 0 }} onClick={logout} data-testid="logout-btn">
              Logout
            </button>
          </div>

          <div className="admin-tabs" data-testid="admin-tabs">
            <button className={`admin-tab ${tab === "results" ? "active" : ""}`} onClick={() => setTab("results")} data-testid="tab-results">
              Results
            </button>
            <button className={`admin-tab ${tab === "messages" ? "active" : ""}`} onClick={() => setTab("messages")} data-testid="tab-messages">
              Messages
              {unread > 0 && <span className="tab-badge" data-testid="unread-badge">{unread}</span>}
            </button>
          </div>

          {msg && <div style={{ color: "#0be881", fontSize: 13, marginBottom: 12, textAlign: "center" }} data-testid="admin-flash">{msg}</div>}

          {tab === "messages" ? (
            <ContactInbox onUnread={setUnread} flash={flash} />
          ) : (
            <>
          <div className="admin-toolbar">
            <div className="date-picker-row">
              <span className="admin-label" style={{ margin: 0 }}>Date:</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="date-picker" />
            </div>
            <div className="toggle-row">
              <div className={`switch ${autoMode ? "on" : ""}`} onClick={toggleAuto} data-testid="auto-mode-toggle">
                <div className="knob" />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Automatic Mode</span>
            </div>
            <button className="admin-btn secondary" style={{ width: "auto", padding: "10px 20px", margin: 0 }} onClick={autoFill} data-testid="auto-fill-btn">
              Auto-Fill Today
            </button>
          </div>

          <div className="slot-grid">
            {slots.map((slot) => {
              const filled = inputs[slot] !== "" && inputs[slot] !== undefined;
              return (
                <div key={slot} className={`slot-card ${filled ? "filled" : ""}`} data-testid={`slot-${slot}`}>
                  <div className="slot-time">{slot}</div>
                  <input
                    className="slot-input"
                    type="number"
                    min="0"
                    max="9"
                    value={inputs[slot] ?? ""}
                    onChange={(e) => setInputs((p) => ({ ...p, [slot]: e.target.value.slice(-1) }))}
                    data-testid={`input-${slot}`}
                  />
                  <div className="slot-actions">
                    <button className="mini-btn save" onClick={() => saveSlot(slot)} data-testid={`save-${slot}`}>Save</button>
                    <button className="mini-btn clear" onClick={() => clearSlot(slot)} data-testid={`clear-${slot}`}>Clear</button>
                  </div>
                </div>
              );
            })}
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
